/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel, ROW_DIRTY_CLASS } from '@gh/common-internal-shared';
import { getRouteInitData, queryFormEditable, State } from '@gh/common-shared';
import { STYLES_FLEX_COLUMN_LAYOUT } from '@gh/core-ui';
import {
  buildEditableAutocompleteColumn,
  FocusedCell,
  gridCollectionComparator,
  GridDefinition,
  GridDefinitionFactory,
  LayoutBasedGridDefinition,
} from '@gh/core-ui-grid';
import { toBoolean } from '@gh/core-util';
import { map$ } from '@gh/rx/operators';
import { negate, without } from 'lodash';
import { Observable } from 'rxjs';
import { JobCsDetailsBusinessService } from '../../../resources/job-cs-details-business.service';
import {
  getJobCsDetailsCcId,
  JobCsDetailsCc,
  jobCsDetailsCcComparator,
  jobCsDetailsCcFieldState,
  validateJobCsDetailsCcField,
} from '../../../resources/job-cs-details.model';
import { JobCsDetailsCommandService } from '../job-cs-details-command.service';
import {
  JobCsDetailsState,
  queryJobCsCurrentCostCenters,
  queryJobCsFocusedCcCell,
  queryJobCsSelectedItemId,
} from '../job-cs-details.state';

@Component({
  standalone: false,
  selector: 'gh-job-cs-details-cc-list',
  templateUrl: 'job-cs-details-cc-list.component.html',
  styles: [STYLES_FLEX_COLUMN_LAYOUT],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCsDetailsCcListComponent implements OnInit {
  costCenters$: Observable<JobCsDetailsCc[]>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  hasSelectedParent$: Observable<boolean>;

  gridDefinition: GridDefinition;

  constructor(private route: ActivatedRoute,
              private business: JobCsDetailsBusinessService,
              private state: State<JobCsDetailsState>,
              private commands: JobCsDetailsCommandService,
              private gridDefinitionFactory: GridDefinitionFactory) {

  }

  get editable(): boolean {
    return this.state.project(queryFormEditable);
  }

  ngOnInit(): void {
    this.gridDefinition = this.buildGridDefinition();

    this.costCenters$ = this.state.select(queryJobCsCurrentCostCenters);
    this.focusedCell$ = this.state.select(queryJobCsFocusedCcCell);
    this.hasSelectedParent$ = this.state.select(queryJobCsSelectedItemId).pipe(map$(negate(Boolean)));
  }

  onAdd(): void {
    this.commands.costCenterAdd();
  }

  onRemove(cc: JobCsDetailsCc): void {
    this.commands.costCenterRemove(cc);
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  private buildGridDefinition(): LayoutBasedGridDefinition {
    const { costCenterGridLayout } = getRouteInitData(this.route);

    const model = createDefaultGridModel({
      editable: this.state.project(queryFormEditable),
      getId: getJobCsDetailsCcId,
      validate: validateJobCsDetailsCcField,
      getCellDef: jobCsDetailsCcFieldState,
      command: (entity, name, value) => this.commands.setCostCenterField(getJobCsDetailsCcId(entity), name, value),
    });

    return this.gridDefinitionFactory.buildLayoutBasedWithModel({
      options: {
        defaultColDef: {
          sortable: false,
        },
        rowClassRules: { [ROW_DIRTY_CLASS]: ({ data }: { data: JobCsDetailsCc }) => toBoolean(data.isDirty) },
      },
      columns: {
        costCenter: {
          ...buildEditableAutocompleteColumn(
            {
              nameProperty: 'name',
              displayProperty: 'display',
              loadMore: (event, { data }) => event.load(this.business.findCostCentersByName(
                event.query,
                // we exclude from autocomlete results all CC in the current subheading, except selected in this cell
                without(
                  this.state.project(queryJobCsCurrentCostCenters).map((cc) => cc.costCenterId).filter(Boolean),
                  data.costCenterId))),
            },
            model.getCellAccessor('costCenter')),
          sort: 'asc',
          comparator: gridCollectionComparator(jobCsDetailsCcComparator),
        },
      },
      model,
      layout: costCenterGridLayout,
    });
  }
}
